if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.initProductForm();
      }

      disconnectedCallback() {
        this.clearLoadFailsafe();
        if (this.form && this._boundSubmit) {
          this.form.removeEventListener('submit', this._boundSubmit);
        }
        if (this._boundClick) this.removeEventListener('click', this._boundClick, true);
      }

      initProductForm() {
        if (this._urlarProductFormInit) {
          return;
        }
        this.form = this.querySelector('form');
        if (!this.form) {
          return;
        }
        this._urlarProductFormInit = true;

        const vi = this.form.querySelector('[name=id]');
        if (vi) vi.disabled = false;
        this._boundSubmit = this.onSubmitHandler.bind(this);
        this._boundClick = this.onButtonClick.bind(this);
        this.form.addEventListener('submit', this._boundSubmit);
        this.addEventListener('click', this._boundClick, true);
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton?.querySelector('span');

        if (document.querySelector('cart-drawer') && this.submitButton) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      getRoutes() {
        return window.routes && typeof window.routes === 'object' ? window.routes : {};
      }

      refreshSubmitButton() {
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton?.querySelector('span');
        if (this.submitButton && document.querySelector('cart-drawer')) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }
      }

      clearLoadFailsafe() {
        if (this._loadFailsafeTimeout != null) {
          clearTimeout(this._loadFailsafeTimeout);
          this._loadFailsafeTimeout = null;
        }
      }

      resetAddToCartLoader() {
        this.refreshSubmitButton();
        this.submitButton?.classList.remove('loading');
        if (this.submitButtonText) {
          this.submitButtonText.textContent =
            this._originalButtonText || window.variantStrings?.addToCart || 'Add to cart';
        }
        if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
        if (!this.error) this.submitButton?.removeAttribute('aria-disabled');
        const spinner = this.querySelector('.loading__spinner');
        if (spinner) spinner.classList.add('hidden');
      }

      updateCartCountUi(cartData) {
        const count = cartData?.item_count ?? 0;
        const countStr = count < 100 ? String(count) : '99+';
        const srLabel = `${count} ${count === 1 ? 'item' : 'items'}`;
        const bubbleMarkup = `<span aria-hidden="true">${countStr}</span><span class="visually-hidden">${srLabel}</span>`;

        const cartLink = document.querySelector('#cart-icon-bubble');
        if (count > 0 && cartLink && !cartLink.querySelector('.cart-count-bubble')) {
          const el = document.createElement('div');
          el.className = 'cart-count-bubble';
          cartLink.appendChild(el);
        }

        document.querySelectorAll('.cart-count-bubble').forEach((bubbleEl) => {
          if (count === 0) {
            bubbleEl.remove();
          } else {
            bubbleEl.innerHTML = bubbleMarkup;
          }
        });

        document.querySelectorAll('[data-cart-count]').forEach((el) => {
          el.textContent = countStr;
        });
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: cartData }));
      }

      fetchCartAndUpdateCount() {
        const r = this.getRoutes();
        const cartJsUrl =
          r?.cart_js_url || `${(r?.cart_url || window.routes?.cart_url || '/cart').replace(/\/$/, '')}.js`;
        return fetch(cartJsUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
          .then((res) => res.json())
          .then((cart) => {
            this.updateCartCountUi(cart);
            return cart;
          })
          .catch(() => {});
      }

      onButtonClick(evt) {
        if (!this._urlarProductFormInit) {
          return;
        }
        const btn = evt.target.closest('button[type="submit"][name="add"]');
        if (!btn || btn.getAttribute('aria-disabled') === 'true' || btn.hasAttribute('disabled')) {
          return;
        }
        this.refreshSubmitButton();
        this._originalButtonText = this.submitButtonText?.textContent || window.variantStrings?.addToCart || 'Add to cart';
        btn.classList.add('loading');
        const span = btn.querySelector('span');
        if (span) {
          span.textContent = window.variantStrings?.addingToCart || 'Adding to Cart';
          span.classList.remove('hidden');
        }
        const spinner = btn.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');

        // Optional user-gesture AMP cart open for stricter in-app browsers.
        if (window.urlarAmpOpenOnClick === true) {
          document.dispatchEvent(new CustomEvent('urlar:cart-open', { bubbles: true }));
        }
      }

      onSubmitHandler(evt) {
        if (!this._urlarProductFormInit) {
          return;
        }
        evt.preventDefault();
        this.refreshSubmitButton();
        if (!this.submitButton || this.submitButton.getAttribute('aria-disabled') === 'true') {
          return;
        }

        if (!this.cart) {
          this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        }

        this.handleErrorMessage();

        const formData = new FormData(this.form);
        const variantIdRaw = formData.get('id');
        const variantIdNum = parseInt(variantIdRaw, 10);
        if (!variantIdRaw || Number.isNaN(variantIdNum)) {
          this.handleErrorMessage(window.variantStrings?.unavailable || 'Please select a valid option.');
          return;
        }

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        if (this.submitButtonText) {
          const addingLabel = window.variantStrings?.addingToCart || 'Adding to Cart';
          if (this.submitButtonText.textContent !== addingLabel) {
            this._originalButtonText = this.submitButtonText.textContent;
          }
          this.submitButtonText.textContent = addingLabel;
        }
        const spinner = this.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');

        const jsonBody = {
          items: [{ id: variantIdNum, quantity: parseInt(formData.get('quantity'), 10) || 1 }],
        };

        const sellingPlan = formData.get('selling_plan');
        if (sellingPlan) jsonBody.items[0].selling_plan = sellingPlan;

        for (const [key, value] of formData.entries()) {
          const match = key.match(/^properties\[(.+)\]$/);
          if (match) {
            if (!jsonBody.items[0].properties) jsonBody.items[0].properties = {};
            jsonBody.items[0].properties[match[1]] = value;
          }
        }

        if (this.cart) {
          jsonBody.sections = this.cart.getSectionsToRender().map((section) => section.id);
          jsonBody.sections_url = window.location.pathname;
          this.cart.setActiveElement(document.activeElement);

          // Optimistic drawer open: cart-drawer renders current cart contents server-side on
          // page load, so opening early shows the user their existing items immediately. When
          // /cart/add returns (~4.6s on this store), renderContents replaces the line list with
          // the post-add state including the new item. The 'is-adding' class lets CSS show a
          // subtle loading affordance during the wait.
          if (typeof this.cart.open === 'function' && !this.cart.classList.contains('active')) {
            this.cart.classList.add('is-adding');
            try {
              this.cart.open(this.submitButton);
            } catch (_) { /* ignore */ }
          }
        }

        const config = fetchConfig();
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        config.body = JSON.stringify(jsonBody);

        const r = this.getRoutes();
        const addBase = (r.cart_add_url || '/cart/add').replace(/\/$/, '').replace(/\.js$/i, '');
        const cartAddUrl = `${addBase}.js`;

        this.clearLoadFailsafe();
        const FAILSAFE_MS = 15000;
        this._loadFailsafeTimeout = setTimeout(() => {
          this._loadFailsafeTimeout = null;
          this.resetAddToCartLoader();
        }, FAILSAFE_MS);

        fetch(cartAddUrl, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantIdRaw,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText?.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = (this.getRoutes()?.cart_url || window.routes?.cart_url || '/cart');
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantIdRaw,
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              return new Promise((resolve) => {
                document.body.addEventListener(
                  'modalClosed',
                  () => {
                    setTimeout(() => {
                      this.cart.renderContents(response);
                      Promise.resolve(this.fetchCartAndUpdateCount()).finally(() => resolve());
                    });
                  },
                  { once: true }
                );
                quickAddModal.hide(true);
              });
            }
            this.cart.renderContents(response);
            // Background count refetch: cart-icon-bubble is already rendered via renderContents,
            // but third-party listeners on the `cart:refresh` event still get a notification.
            // Don't return — the spinner cleanup in .finally() shouldn't block on a /cart.js fetch
            // that takes ~750ms after the drawer is already populated.
            this.fetchCartAndUpdateCount();
          })
          .catch((e) => {
            console.error(e);
            this.handleErrorMessage(window.cartStrings?.error || 'Could not add to cart. Please try again.');
          })
          .finally(() => {
            this.clearLoadFailsafe();
            this.resetAddToCartLoader();
            // If the add failed (sold out, etc.), close the eagerly-opened drawer so the user
            // isn't left looking at a stale cart with the error message somewhere offscreen.
            if (this.error && this.cart?.classList.contains('is-adding')) {
              this.cart.classList.remove('is-adding');
              if (typeof this.cart.close === 'function') this.cart.close();
            }
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (!this.submitButton) this.refreshSubmitButton();
        if (!this.submitButton) return;
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text && this.submitButtonText) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          if (this.submitButtonText) this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form?.querySelector('[name=id]');
      }
    }
  );
}
